/*
 * Draws the Box folder access-check result inside the add modal's folder input, so the
 * modal itself stays focused on input, exclusions, and enrollment. Also owns the probe
 * state vocabulary the modal derives and renders against.
 */
import React from 'react';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Tooltip from '@amzn/meridian/tooltip';
import alertKnockoutTokens from '@amzn/meridian-tokens/base/icon/alert-knockout';
import checkCircleTokens from '@amzn/meridian-tokens/base/icon/check-circle';
import { type TranslateFn } from 'src/arb-wrapper';
import { type BoxFolder } from 'src/types/sync-target.types';

export type ProbeState = 'empty' | 'unparseable' | 'debouncing' | 'checking' | 'ok' | 'inaccessible' | 'unavailable';

/** Only `ok` permits enrollment; every other state has a reason to show. */
const PROBE_MESSAGE_KEYS: Partial<Record<ProbeState, string>> = {
  unparseable: 'box_folder_unparseable',
  inaccessible: 'box_folder_inaccessible_short',
  unavailable: 'box_folder_unavailable',
};

interface ProbeIndicatorArgs {
  readonly folder: BoxFolder | undefined;
  readonly probeState: ProbeState;
  readonly t: TranslateFn;
}

const getProbeIndicatorContent = ({ folder, probeState, t }: ProbeIndicatorArgs): React.ReactNode => {
  if (probeState === 'checking') {
    return <Loader data-testid="add-box-folder-checking" size="small" />;
  }

  if (probeState === 'ok') {
    return (
      <Tooltip
        position="top"
        title={t('box_folder_verified', {
          name: folder?.name ?? '',
          count: String(folder?.itemCollection?.totalCount ?? 0),
        })}
      >
        {/* Tooltip needs a DOM node to anchor to; Icon does not forward a ref. */}
        <div data-testid="add-box-folder-verified" style={{ display: 'inline-flex' }}>
          <Icon color="success" tokens={checkCircleTokens} />
        </div>
      </Tooltip>
    );
  }

  const messageKey = PROBE_MESSAGE_KEYS[probeState];
  if (!messageKey) {
    return undefined;
  }

  return (
    <Tooltip position="top" title={t(messageKey)}>
      <div data-testid="add-box-folder-invalid" style={{ display: 'inline-flex' }}>
        <Icon color="error" tokens={alertKnockoutTokens} />
      </div>
    </Tooltip>
  );
};

/** Returns `undefined` rather than an empty node so the input renders no suffix at all. */
export const getProbeIndicator = (args: ProbeIndicatorArgs): React.ReactNode => {
  const content = getProbeIndicatorContent(args);
  if (!content) {
    return undefined;
  }

  // Meridian wraps only a *string* suffix in its own endcap; a node is dropped straight
  // into the input box, which is `display: flex` with no `alignItems` — so a bare node
  // stretches the full height and paints at the top, flush to the border. This mirrors
  // what Meridian's own `suffixIconTokens` path does: center it, and inset it from the
  // right edge.
  return (
    <Row alignmentVertical="center" spacingInset="none 300 none none">
      {content}
    </Row>
  );
};
